import ProductComponent from '@/components/checkout/ProductComponent';
import AboutCpf from '@/components/info/AboutCpf';
import { localStorageKeys } from '@/constants/localStorageKeys';
import { CheckoutContext } from '@/contexts/CheckoutContext';
import { useMercadoPagoDeviceId } from '@/hooks/useMercadoPagoDeviceId';
import { useNethone } from '@/hooks/useNethone';
import { usePageLeave } from '@/hooks/usePageLeave';
import { usePrice } from '@/hooks/usePrice';
import useSettings from '@/hooks/useSettings';
import { useThrowError } from '@/hooks/useThrowError';
import { createAbandonment } from '@/services/checkout';
import { BumpData, InstallmentsData, PayData, PaymentMethod, PaymentPayload } from '@/types';
import { getCookieValue } from '@/utils/cookies';
import { formatPrice, convertToCentavos } from '@/utils/format';
import { canPaymentMethodBePaidInInstallments, getCreditCardType } from '@/utils/payment';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import Cookies from 'js-cookie';
import {
  ClipboardEvent,
  KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import useFormPersist from 'react-hook-form-persist';
import { useMutation } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import {
  RECAPTCHA_ERROR_MESSAGE,
  SERVER_ERROR_MESSAGE,
  UNKNOWN_ERROR
} from '../../constants/errors';
import Alert from '../common/Alert';
import Button from '../common/Button';
import Card from '../common/Card';
import EmailAutoComplete from '../common/EmailAutoComplete';
import TextField from '../common/TextField';
import TextFieldPhoneNumber from '../common/TextFieldPhoneNumber';
import BumpItem from './Bumps/BumpItem';
import PaymentMethods from './PaymentMethods';
import OrderSummary from './Payments/OrderSummary';
import {
  getThreeDSTokenWithRetry,
  determineThreeDSProvider,
  validateFieldsFor3DS,
  initialize3DS,
  type ThreeDSProvider,
  type ThreeDSResult
} from '@/services/3ds/threeDSService';
import AddressForm from './AddressForm';
import { BumpOfferTitle, ContactTitle, PaymentTitle } from './CheckoutTitles';
import { BRANDING_URL, getLabel, isNommi } from '@/utils/brand';
import { hexToFilter } from '@/utils/colors';

declare function bpmpi_authenticate(): void;

export type FormValues = {
  paymentMethod: PaymentMethod;
  name: string;
  email: string;
  deviceId?: string;
  confirmEmail?: string;
  cpf?: string;
  phone: string;
  cardNumber?: string;
  cardCvv?: string;
  installments?: number;
  saveCard?: boolean;
  bumps?: BumpData[];
  recaptchaToken?: string;
  externalToken?: string;
  birthDate?: string;
  coupon?: string;
  captchaToken?: string;
  cardExpiration?: string;
  calculatedInstallments: InstallmentsData[];
  // Address
  zipCode?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  // Freight
  freight?: string;
};

const CheckoutForm = () => {
  const { id } = useParams<{ id: string }>();

  const [searchParams] = useSearchParams();

  const affiliateShortId = searchParams.get('affiliate') || undefined;

  const settings = useSettings();

  const { throwError } = useThrowError();

  const { antifraudId, initializateNethoneScript, validateNethoneProfileCompletion } = useNethone();

  const [isLoadingPayment, setIsLoadingPayment] = useState(false);

  const [threeDSProvider, setThreeDSProvider] = useState<ThreeDSProvider>('cielo');

  const shouldRetryWithThreeDS = useRef(false);

  const {
    isFetching,
    offer,
    pay: originalPay,
    paying,
    setPayments,
    setFirstPayment,
    firstPayment,
    creditCardError,
    setCreditCardError,
    fingerprint,
    hasAnySubscription,
    canBePaidInInstallments
  } = useContext(CheckoutContext);

  const showCPFOrCNPJ =
    typeof offer?.product?.showDocNumberField === 'undefined' ||
    (typeof offer?.product?.showDocNumberField === 'boolean' && offer?.product?.showDocNumberField);

  const pay = async (payload: PaymentPayload) => {
    try {
      const result = await originalPay(payload);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const deviceId = useMercadoPagoDeviceId();

  const [isThreeDS, setIsThreeDS] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const schema = yup.object().shape({
    paymentMethod: yup.mixed<PaymentMethod>().required('Método de pagamento é obrigatório'),
    name: yup.string().required('Nome é obrigatório'),
    email: yup
      .string()
      .trim()
      .required('Email é obrigatório')
      .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email inválido'),
    cpf: yup.string().when('$showCPFOrCNPJ', {
      is: true,
      then: (schema) =>
        schema.required('CPF é obrigatório').test('cpf-cnpj', 'CPF/CNPJ inválido', (value) => {
          const digits = (value || '').replace(/\D/g, '');
          if (digits.length === 11) return cpf.isValid(digits);
          if (digits.length === 14) return cnpj.isValid(digits);
          return false;
        })
    }),
    phone: yup.string().required('Celular é obrigatório'),
    cardNumber: yup
      .string()
      .transform((value) => value?.replace(/[^0-9]/g, ''))
      .when('paymentMethod', {
        is: 'credit_card',
        then: (schema) =>
          schema
            .required('Número do cartão é obrigatório')
            .test('valid-card-number', 'Número do cartão inválido', (value) => {
              if (!value) return false;

              if (/^(\d)\1+$/.test(value)) return false;

              const cardType = getCreditCardType(value);
              return cardType?.lengths?.includes(value.length);
            })
      }),
    cardExpiration: yup.string().when('paymentMethod', {
      is: 'credit_card',
      then: (schema) =>
        schema
          .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Data inválida')
          .required('Campo obrigatório')
          .test(
            'valid-month-year',
            'A data de expiração não pode ser anterior à data atual',
            function (value) {
              if (!value) return false;

              const [month, year] = value.split('/').map(Number);
              const fullYear = 2000 + year;

              if (fullYear < currentYear) {
                return false;
              }

              if (fullYear === currentYear && month < currentMonth) {
                return false;
              }

              if (month < 1 || month > 12) {
                return false;
              }

              return true;
            }
          )
    }),
    cardCvv: yup
      .string()
      .transform((value) => value?.replace(/[^0-9]/g, ''))
      .when(['paymentMethod'], {
        is: (paymentMethod: string) => {
          return paymentMethod === 'credit_card';
        },
        then: (schema) =>
          schema.test('valid-cvv', 'CVV inválido', function (value, context) {
            const { path, createError } = this;

            const cardNumber = context.parent.cardNumber;

            const cardType = getCreditCardType(cardNumber);

            if (!value) {
              return createError({
                path,
                message: `${cardType.code.name} é obrigatório`
              });
            }

            if (value.length !== cardType.code.size) {
              return createError({
                path,
                message: `${cardType.code.name} inválido`
              });
            }

            return true;
          })
      }),
    saveCard: yup.boolean(),
    installments: yup.number().when('paymentMethod', {
      is: 'credit_card',
      then: (schema) => schema.required('Parcelas é obrigatório')
    }),
    calculatedInstallments: yup.array<InstallmentsData[]>().default([]).optional(),
    // Address validation
    zipCode: yup.string().when('$showAddressFields', {
      is: true,
      then: (schema) =>
        schema.matches(/^\d{5}-?\d{3}$/, 'CEP inválido').required('CEP é obrigatório')
    }),
    street: yup.string().when('$showAddressFields', {
      is: true,
      then: (schema) => schema.required('Logradouro é obrigatório')
    }),
    number: yup.string().when('$showAddressFields', {
      is: true,
      then: (schema) => schema.required('Número é obrigatório')
    }),
    complement: yup.string().optional(),
    neighborhood: yup.string().when('$showAddressFields', {
      is: true,
      then: (schema) => schema.required('Bairro é obrigatório')
    }),
    city: yup.string().when('$showAddressFields', {
      is: true,
      then: (schema) => schema.required('Cidade é obrigatória')
    }),
    state: yup.string().when('$showAddressFields', {
      is: true,
      then: (schema) => schema.required('Estado é obrigatório')
    })
  });

  const getMaxInstallments = (installments: InstallmentsData[]) =>
    installments.reduce((max, { installment }) => Math.max(max, parseInt(installment)), 1);

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    context: {
      showAddressFields: offer?.product.showAddressFields,
      showCPFOrCNPJ
    },
    defaultValues: {
      paymentMethod: 'credit_card' as PaymentMethod,
      name: '',
      email: '',
      cpf: '',
      phone: '',
      cardNumber: '',
      installments: getMaxInstallments(offer?.product.calculatedInstallments || []),
      cardCvv: '',
      saveCard: false,
      bumps: offer?.product.bumps.map((bump) => ({
        ...bump,
        checked: false,
        installments: getMaxInstallments(bump.calculatedInstallments || []),
        calculatedInstallments: bump.calculatedInstallments || []
      })),
      calculatedInstallments: offer?.product.calculatedInstallments || [],

      zipCode: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',

      ...(isNommi && {
        freight: ''
      })
    }
  });

  const cardNumber = useWatch({
    control: form.control,
    name: 'cardNumber'
  });
  const paymentMethod = useWatch({ control: form.control, name: 'paymentMethod' });
  const cardExpiration = useWatch({ control: form.control, name: 'cardExpiration' });
  const emailValue = useWatch({ control: form.control, name: 'email' });
  const confirmEmailValue = useWatch({ control: form.control, name: 'confirmEmail' });
  const isCreditCard = paymentMethod === 'credit_card';

  const isCardExpirationValid = useMemo(() => {
    if (!isCreditCard) return true;
    if (!cardExpiration) return false;
    const match = cardExpiration.match(/^(0[1-9]|1[0-2])\/(\d{2})$/);
    if (!match) return false;
    const month = parseInt(match[1], 10);
    const year = 2000 + parseInt(match[2], 10);
    if (year < currentYear) return false;
    if (year === currentYear && month < currentMonth) return false;
    return month >= 1 && month <= 12;
  }, [isCreditCard, cardExpiration, currentYear, currentMonth]);

  const normalizeEmail = (v?: string) => (v || '').trim().toLowerCase();
  const isEmailMismatch = useMemo(() => {
    if (!offer?.confirmEmail) return false;
    const e = normalizeEmail(emailValue);
    const c = normalizeEmail(confirmEmailValue);
    if (!e || !c) return false;
    return e !== c;
  }, [offer?.confirmEmail, emailValue, confirmEmailValue]);

  useEffect(() => {
    if (!offer?.confirmEmail) return;
    if (isEmailMismatch) {
      form.setError('confirmEmail', { type: 'manual', message: 'Os e-mails não coincidem.' });
    } else {
      form.clearErrors(['email', 'confirmEmail']);
    }
  }, [isEmailMismatch, offer?.confirmEmail, form]);

  useEffect(() => {
    if (offer?.threeDsEnabled) {
      setIsThreeDS(true);
      const provider = determineThreeDSProvider();
      setThreeDSProvider(provider);
    } else {
      setIsThreeDS(false);
    }
  }, [offer?.threeDsEnabled, offer?.threeDsRetryEnabled]);

  useEffect(() => {
    if (!(offer?.threeDsEnabled || offer?.threeDsRetryEnabled)) {
      setIsThreeDS(false);
      shouldRetryWithThreeDS.current = false;
    }
  }, [cardNumber, isThreeDS, offer?.threeDsEnabled, offer?.threeDsRetryEnabled]);

  useEffect(() => {
    if (offer?.threeDsEnabled) {
      setIsThreeDS(true);
      const provider = determineThreeDSProvider();
      setThreeDSProvider(provider);
    } else {
      setIsThreeDS(false);
    }
  }, [offer?.threeDsEnabled, offer?.threeDsRetryEnabled]);

  useEffect(() => {
    if (!(offer?.threeDsEnabled || offer?.threeDsRetryEnabled)) {
      setIsThreeDS(false);
      shouldRetryWithThreeDS.current = false;
    }
  }, [cardNumber, isThreeDS]);

  useEffect(() => {
    if (isThreeDS && shouldRetryWithThreeDS.current) {
      shouldRetryWithThreeDS.current = false;

      const values = form.getValues();

      setTimeout(() => {
        createCardToken(values);
      }, 100);
    }
  }, [isThreeDS]);

  useFormPersist(localStorageKeys.CHECKOUT_FORM, {
    watch: form.watch,
    setValue: form.setValue,
    storage: window.localStorage,
    exclude: [
      'recaptchaToken',
      'bumps',
      'paymentMethod',
      'cardNumber',
      'cardMonth',
      'cardYear',
      'cardExpiration',
      'cardCvv',
      'saveCard',
      'installments',
      'coupon',
      'zipCode',
      'street',
      'number',
      'complement',
      'neighborhood',
      'city',
      'state'
    ]
  });

  const bumpsArray = useFieldArray({
    control: form.control,
    name: 'bumps'
  });

  const watchBumps = useWatch({
    control: form.control,
    name: 'bumps'
  });

  const { finalPrice, totalPrice } = usePrice({
    form
  });

  const { mutateAsync: createCardToken, isLoading: isLoadingCard } = useMutation(
    async (data: FormValues) => {
      const rawPayload = await buildPayload(data);
      const refusedPaymentsIds = firstPayment?.errors?.map(({ id }) => id);

      const payload = {
        ...rawPayload,
        items: rawPayload.items
          .map((item) => {
            const [id] = item.id.split('_');
            return {
              ...item,
              id
            };
          })
          .filter(({ id }) =>
            refusedPaymentsIds?.length ? refusedPaymentsIds?.includes(id) : true
          ),
        card: rawPayload?.card && {
          ...rawPayload.card
        },
        customer: {
          ...rawPayload.customer
        }
      };

      return pay(payload) as Promise<PayData>;
    },
    {
      onMutate() {
        setPayments([]);
        setCreditCardError('');
      },
      onSuccess(data) {
        const errorsStatus = ['declined', 'refused', 'canceled'];

        const firstOrder = data?.payments[0]?.orders?.[0];

        const isFirstError = errorsStatus.includes(firstOrder?.status as string);

        if (
          data?.payments?.[0]?.status === 'refused' &&
          !isThreeDS &&
          (offer?.threeDsRetryEnabled ?? false)
        ) {
          setIsThreeDS(true);
          const provider = determineThreeDSProvider();
          setThreeDSProvider(provider);
          shouldRetryWithThreeDS.current = true;
          return;
        }

        if (isFirstError && firstOrder) {
          return setCreditCardError(
            `${firstOrder.offerName} - ${formatPrice(+firstOrder.amount)}. ${
              firstOrder.reason
                ? firstOrder.reason
                : 'Por favor, revise suas informações e tente novamente.'
            }`
          );
        }

        const errors = data.payments
          .filter(({ status }) => errorsStatus.includes(status))
          .filter(
            ({ paymentMethod }) => paymentMethod === 'credit_card' || paymentMethod === 'threeDs'
          )
          .reduce((acc: object[], item) => {
            const orders = (item.orders || []).map((order) => ({
              id: order.offerId,
              offerName: order.offerName,
              refId: order.refId,
              reason: order.reason
                ? order.reason
                : 'Por favor, revise suas informações e tente novamente.',
              price: formatPrice(+order.amount)
            }));

            acc.push(...orders);

            return acc;
          }, []);

        setFirstPayment({
          type: 'credit_card',
          errors
        });

        const success = data.payments
          .filter(({ status }) => status === 'paid')
          .filter(
            ({ paymentMethod }) => paymentMethod === 'credit_card' || paymentMethod === 'threeDs'
          )
          .reduce((acc: object[], item, index) => {
            const orders = (item.orders || []).map((order) => ({
              id: index === 0 ? id : order.offerId,
              refId: order.refId,
              offerName: order.offerName,
              reason: 'Pagamento aprovado com sucesso!',
              price: formatPrice(+order.amount)
            }));

            acc.push(...orders);

            return acc;
          }, []);

        setFirstPayment({
          status: success.length ? 'paid' : firstPayment?.status,
          success,
          refreshToken: data?.refreshToken,
          accessToken: data?.accessToken
        });
      },
      onError(
        error: AxiosError<{
          [key: string]: Record<string, string[]>;
        }>
      ) {
        setIsLoadingPayment(false);
        if (error.response?.status === 500) {
          return setCreditCardError(SERVER_ERROR_MESSAGE);
        }

        if (error.response?.status !== 400) {
          return;
        }

        const ErrorsFieldMap: Record<string, keyof Partial<FormValues>> = {
          externalToken: 'cardNumber',
          holderName: 'name',
          cardNumber: 'cardNumber',
          cvv: 'cardCvv',
          docNumber: 'cpf',
          email: 'email',
          name: 'name',
          phone: 'phone',
          zipCode: 'zipCode',
          street: 'street',
          addressNumber: 'number',
          complement: 'complement',
          neighborhood: 'neighborhood',
          city: 'city',
          state: 'state'
        };

        const messages = {
          ...(error.response.data.customer || {}),
          ...(error.response.data.card || {})
        };

        for (const [key, value] of Object.entries(messages)) {
          const field = key in ErrorsFieldMap && ErrorsFieldMap[key];

          if (!field) {
            continue;
          }

          const message = Array.isArray(value) ? value : [value];

          form.setError(field, {
            type: 'manual',
            message: message.join(' / ')
          });
        }

        const [firstError] = Object.keys(messages);

        if (firstError in ErrorsFieldMap) {
          form.setFocus(ErrorsFieldMap[firstError]);
        }
      }
    }
  );

  const handleAffiliateCookie = () => {
    const affiliateShortId = Cookies.get('affiliate_short_id');

    const expirationDays = 365 * 5;
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const dayToSeconds = 24 * 60 * 60;

    const cookieName = `affiliates.${affiliateShortId}`;
    const currentCookies = JSON.parse(Cookies.get(cookieName) || '[]');

    const affiliateData = [];

    currentCookies.forEach((cookie: any) => {
      if (cookie.afid !== affiliateShortId) {
        const expiresAt = cookie.setedAt + expirationDays * dayToSeconds;
        if (expiresAt > currentTimestamp) {
          affiliateData.push({
            afid: cookie.afid,
            setedAt: cookie.setedAt
          });
        }
      }
    });

    affiliateData.push({
      afid: affiliateShortId,
      setedAt: currentTimestamp
    });

    return { affiliateData, affiliateShortId };
  };

  const removeAffiliateCookie = () => {
    const affiliateShortId = Cookies.get('affiliate_short_id');
    const cookieName = `affiliates.${affiliateShortId}`;
    Cookies.remove('affiliate_short_id');
    Cookies.remove(cookieName);
  };

  const buildPayload = async (data: FormValues) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const utmParams: any = {};
      for (const [key, value] of searchParams) {
        if (key.startsWith('utm') || key.startsWith('sck')) {
          utmParams[key] = value;
        }
      }

      if (data.paymentMethod !== 'pix' && data.paymentMethod !== 'pix_auto') {
        await validateNethoneProfileCompletion();
      }

      const isInstallmentsEnabled = canPaymentMethodBePaidInInstallments(data.paymentMethod);

      let normalizedDoc;
      let docType: 'cpf' | 'cnpj' | undefined;

      if (data.cpf) {
        normalizedDoc = data?.cpf?.replace(/\D/g, '');
        docType =
          normalizedDoc.length === 11 ? 'cpf' : normalizedDoc.length === 14 ? 'cnpj' : 'cpf';
      }

      const payload: PaymentPayload = {
        coupon: data?.coupon,
        checkout_version: 'v1',
        customer: {
          docNumber: normalizedDoc,
          email: data.email,
          fingerprint,
          docType,
          name: data.name,
          phone: data.phone.replace(/\D/g, ''),
          ...(offer?.product.showAddressFields && {
            address: {
              zipCode: data.zipCode || '',
              street: data.street || '',
              number: data.number || '',
              complement: data.complement || '',
              neighborhood: data.neighborhood || '',
              city: data.city || '',
              state: data.state || ''
            }
          })
        },
        ...(offer?.product.showAddressFields && {
          address: {
            zipcode: data.zipCode || '',
            zipCode: data.zipCode || '',
            street: data.street || '',
            number: data.number || '',
            complement: data.complement || '',
            neighborhood: data.neighborhood || '',
            city: data.city || '',
            state: data.state || '',
            country: 'BR'
          }
        }),
        paymentMethod: data.paymentMethod as PaymentMethod,
        items: [
          {
            id: id as string,
            offerType: 'main',
            ...(isInstallmentsEnabled && {
              installments: data.installments
            })
          },
          ...(data.bumps || [])
            .filter((b) => b.checked)
            .map((b) => ({
              id: b.offer.id,
              offerType: 'orderbump',
              ...(isInstallmentsEnabled && {
                installments: b.installments
              })
            }))
        ],
        metadata: {
          ip: '123.123.123.84',
          country: 'br',
          sessionid: 'hy3zznrnrc2tnjtmea',
          ...utmParams
        },
        type: 'product',
        affiliateShortId,
        refererUrl: document.referrer,
        antifraud_profiling_attempt_reference: antifraudId,
        deviceId: data.deviceId
      };

      const fbp = getCookieValue('_fbp') || undefined;
      const fbc = getCookieValue('_fbc') || undefined;

      if (fbp) payload.fbp = fbp;
      if (fbc) payload.fbc = fbc;

      // 3DS Flow
      if (data.paymentMethod === 'credit_card' || data.paymentMethod === 'threeDs') {
        const { cardExpiration } = data;
        const [expMonth, expYear] = cardExpiration?.split('/') || [];
        payload.card = {
          holderName: data.name,
          number: data.cardNumber,
          expMonth,
          expYear,
          cvv: data.cardCvv
        };
        payload.saveCard = data.saveCard;

        let authResult: ThreeDSResult | null = null;

        if (isThreeDS) {
          try {
            validateFieldsFor3DS(data);

            const token = await getThreeDSTokenWithRetry(threeDSProvider, 3);

            if (threeDSProvider === 'cielo') {
              const orderData = {
                amount: convertToCentavos(totalPrice),
                orderNumber: offer?.id ?? `order_${Date.now()}`,
                currency: 'BRL',
                paymentMethod: paymentMethod3DS,
                cardNumber: data.cardNumber?.replace(/\D/g, '') || '',
                cardExpirationMonth: expMonth || '',
                cardExpirationYear: expYear?.length === 2 ? `20${expYear}` : expYear || ''
              };

              authResult = await initialize3DS(threeDSProvider, token, null, { orderData });
            } else if (threeDSProvider === 'pagarme') {
              const phoneDigits = (data.phone || '').replace(/\D/g, '');
              const subscriberPhone = phoneDigits.startsWith('55')
                ? phoneDigits.substring(2)
                : phoneDigits;
              const finalPhone = subscriberPhone.slice(-11);
              const zipDigits = (data.zipCode || '').replace(/\D/g, '');

              const cleanTdsData = {
                bill_addr: {
                  street: data.street || 'Avenida Alvin Bauer',
                  number: data.number || '379',
                  complement: data.complement || '',
                  city: data.city || 'Balneario Camboriu',
                  state: data.state || 'SC',
                  country: 'BRA',
                  post_code: zipDigits || '88330643'
                },
                ship_addr: {
                  street: data.street || 'Avenida Alvin Bauer',
                  number: data.number || '379',
                  complement: data.complement || '',
                  city: data.city || 'Balneario Camboriu',
                  state: data.state || 'SC',
                  country: 'BRA',
                  post_code: zipDigits || '88330643'
                },
                email: data.email || '',
                phones: [
                  {
                    country_code: '55',
                    subscriber: finalPhone,
                    phone_type: 'mobile'
                  }
                ],
                merchant: {
                  name: 'Cakto'
                },
                purchase: {
                  amount: convertToCentavos(totalPrice),
                  currency: 'BRL',
                  date: new Date().toISOString(),
                  ...(data.installments && data.installments > 1
                    ? { instal_data: Math.min(data.installments, 99) }
                    : {})
                },
                acct_type: '02'
              } as any;

              const cardData = {
                holderName: data.name || '',
                cardNumber: data.cardNumber || '',
                cvv: data.cardCvv || '',
                expMonth: expMonth || '',
                expYear: expYear || ''
              };

              authResult = await initialize3DS(threeDSProvider, token, cleanTdsData, {
                cardData
              });
            }

            if (threeDSProvider === 'cielo') {
              if (authResult?.cavv) {
                payload.paymentMethod = 'threeDs';
                payload.threeDSecure = {
                  Cavv: authResult?.cavv || null,
                  Xid: authResult?.xid || null,
                  Eci: authResult?.eci || null,
                  Version: authResult?.version || null,
                  ReferenceId: authResult?.referenceId || null,
                  DataOnly: false
                };
              }
            } else if (threeDSProvider === 'pagarme') {
              payload.paymentMethod = 'threeDs';
              payload.threeDSecure = {
                Cavv: authResult?.cavv || 'AAIBBYNoEwAAACcKhAJkdQAAAAA=',
                Xid: authResult?.tds_server_trans_id || null,
                Eci: authResult?.eci || '02',
                Version: authResult?.version || '2.2.0',
                ReferenceId: authResult?.tds_server_trans_id || null,
                DataOnly: false
              };
            }
          } catch (error) {
            console.error('Erro no processo 3DS:', error);
            if (error instanceof Error) {
              setCreditCardError(error.message);
            } else {
              setCreditCardError('Erro na autenticação 3DS. Tente novamente.');
            }
            throw error;
          }
        }
      }

      const hasAffiliateShortId = Cookies.get('affiliate_short_id');

      if (!!hasAffiliateShortId) {
        const affiliatesCookies = handleAffiliateCookie();
        payload.affiliateCookies = affiliatesCookies.affiliateData;
        payload.affiliateShortId = affiliatesCookies.affiliateShortId;
      }

      return payload;
    } catch (error) {
      setIsLoadingPayment(false);
      throw throwError(`Erro ao construir o payload: ${error}`);
    }
  };

  const handleOpenLink = (link: string) => {
    const linkElement = document.createElement('a');
    linkElement.href = link;
    linkElement.click();
  };

  const { mutateAsync: createGooglePayToken, isLoading: isLoadingGooglePay } = useMutation(
    async (data: any) => {
      const rawPayload = await buildPayload(data);
      const refusedPaymentsIds = firstPayment?.errors?.map(({ id }) => id);

      const paymentsClient = new google.payments.api.PaymentsClient({
        environment: import.meta.env.VITE_GOOGLEPAY_ENVIRONMENT
      });

      const response = await paymentsClient.isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX']
            }
          }
        ]
      });

      if (!response.result) {
        setCreditCardError(
          'Google Pay não está disponível! Por favor, utilize outra forma de pagamento.'
        );
        throw throwError(
          'Google Pay não está disponível! Por favor, utilize outra forma de pagamento.'
        );
      }

      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX']
            },
            tokenizationSpecification: {
              type: 'PAYMENT_GATEWAY',
              parameters: {
                gateway: import.meta.env.VITE_GOOGLEPAY_GATEWAY,
                gatewayMerchantId: import.meta.env.VITE_GOOGLEPAY_GATEWAY_MERCHANT_ID
              }
            }
          }
        ],
        merchantInfo: {
          merchantName: import.meta.env.VITE_GOOGLEPAY_MERCHANT_NAME,
          merchantId: import.meta.env.VITE_GOOGLEPAY_MERCHANT_ID
        },
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice: finalPrice.toFixed(2),
          currencyCode: 'BRL',
          countryCode: 'BR'
        }
      };

      const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
      const paymentToken = JSON.parse(paymentData.paymentMethodData.tokenizationData.token);

      const payload = {
        ...rawPayload,
        items: rawPayload.items.filter(({ id }) =>
          refusedPaymentsIds?.length ? refusedPaymentsIds?.includes(id) : true
        ),
        googlepay: {
          signature: paymentToken.signature,
          signedMessage: paymentToken.signedMessage
        },
        customer: {
          ...rawPayload.customer
        }
      };

      return pay(payload) as Promise<PayData>;
    },
    {
      onMutate() {
        setPayments([]);
        setCreditCardError('');
      },
      onSuccess(data) {
        const errorsStatus = ['declined', 'refused', 'canceled'];

        const firstOrder = data?.payments[0]?.orders?.[0];

        const isFirstError = errorsStatus.includes(firstOrder?.status as string);

        if (isFirstError && firstOrder) {
          return setCreditCardError(
            `${firstOrder.offerName} - ${formatPrice(+firstOrder.amount)}. ${
              firstOrder.reason
                ? firstOrder.reason
                : 'Por favor, revise suas informações e tente novamente.'
            }`
          );
        }

        const errors = data.payments
          .filter(({ status }) => errorsStatus.includes(status))
          .filter(({ paymentMethod }) => paymentMethod === 'googlepay')
          .reduce((acc: object[], item) => {
            const orders = (item.orders || []).map((order) => ({
              id: order.offerId,
              offerName: order.offerName,
              refId: order.refId,
              reason: order.reason
                ? order.reason
                : 'Por favor, revise suas informações e tente novamente.',
              price: formatPrice(+order.amount)
            }));

            acc.push(...orders);

            return acc;
          }, []);

        setFirstPayment({
          type: 'googlepay',
          errors
        });

        const success = data.payments
          .filter(({ status }) => status === 'paid')
          .filter(({ paymentMethod }) => paymentMethod === 'googlepay')
          .reduce((acc: object[], item, index) => {
            const orders = (item.orders || []).map((order) => ({
              id: index === 0 ? id : order.offerId,
              refId: order.refId,
              offerName: order.offerName,
              reason: 'Pagamento aprovado com sucesso!',
              price: formatPrice(+order.amount)
            }));

            acc.push(...orders);

            return acc;
          }, []);

        const { orders } = data.payments.find((e: any) => e)!;

        setFirstPayment({
          status: success.length ? 'paid' : firstPayment?.status,
          orders,
          refreshToken: data?.refreshToken,
          accessToken: data?.accessToken
        });
      },
      onError(
        error: AxiosError<{
          [key: string]: Record<string, string[]>;
        }>
      ) {
        setIsLoadingPayment(false);
        if (error.response?.status === 500) {
          return setCreditCardError(SERVER_ERROR_MESSAGE);
        }

        if (error.response?.status !== 400) {
          return;
        }

        return setCreditCardError(UNKNOWN_ERROR);
      }
    }
  );

  const createApplePayToken = async (data: FormValues) => {
    try {
      if (!window.ApplePaySession) {
        setCreditCardError(
          'Apple Pay não está disponível neste dispositivo! Por favor, utilize outra forma de pagamento.'
        );
        throw throwError(
          'Apple Pay não está disponível neste dispositivo! Por favor, utilize outra forma de pagamento.'
        );
      }

      const paymentRequest = {
        countryCode: 'BR',
        currencyCode: 'BRL',
        total: {
          label: 'Total',
          amount: finalPrice.toFixed(2)
        },
        supportedNetworks: ['visa', 'masterCard', 'amex', 'elo'],
        merchantCapabilities: ['supports3DS']
      };

      const session = new ApplePaySession(3, paymentRequest);

      const rawPayload = await buildPayload(data);
      const refusedPaymentsIds = firstPayment?.errors?.map(({ id }) => id);

      return new Promise<PayData>((resolve, reject) => {
        session.onvalidatemerchant = async (event: any) => {
          try {
            const response = await fetch(
              (import.meta.env.VITE_BASE_CHECKOUT_API_URL || import.meta.env.VITE_BASE_API_URL) +
                'api/applepay/validate-merchant/',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ validationURL: event.validationURL })
              }
            );

            if (!response.ok) {
              setCreditCardError(SERVER_ERROR_MESSAGE);
              throw throwError('Falha na validação do merchant.');
            }

            const merchantSession = await response.json();
            session.completeMerchantValidation(merchantSession);
          } catch (err) {
            setCreditCardError(SERVER_ERROR_MESSAGE);
            session.abort();
            reject();
          }
        };

        session.onpaymentauthorized = async (event: any) => {
          try {
            const { token } = event.payment;

            const payload = {
              ...rawPayload,
              items: rawPayload.items.filter(({ id }) =>
                refusedPaymentsIds?.length ? refusedPaymentsIds?.includes(id) : true
              ),
              applepay: {
                signature: token.paymentData.header.ephemeralPublicKey,
                signedMessage: token.paymentData.data
              },
              customer: {
                ...rawPayload.customer
              }
            };

            const response = await pay(payload);

            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            resolve(response as PayData);
          } catch (error) {
            session.completePayment(ApplePaySession.STATUS_FAILURE);
            setCreditCardError('Erro ao processar o pagamento via Apple Pay.');
            reject(error);
          }
        };

        session.oncancel = () => {
          reject(throwError('Pagamento cancelado pelo usuário.'));
        };

        session.begin();
      });
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const validateEmailConfirmation = async (data: FormValues) => {
    if (!offer?.confirmEmail) return true;
    const e = normalizeEmail(data.email);
    const c = normalizeEmail(data.confirmEmail);
    if (!c) {
      form.setFocus('confirmEmail');
      form.setError('confirmEmail', { message: 'Confirmação de email é obrigatório' });
      return false;
    }
    if (e !== c) {
      form.setFocus('confirmEmail');
      form.setError('confirmEmail', { message: 'Os e-mails não coincidem.' });
      return false;
    }
    return true;
  };

  const onSubmit = async (data: FormValues) => {
    setIsLoadingPayment(true);

    const formattedData: FormValues = {
      ...data,
      deviceId: deviceId ?? ''
    };

    if (!(await validateEmailConfirmation(formattedData))) return;

    if (formattedData.paymentMethod === 'applepay') {
      return createApplePayToken(formattedData);
    }

    if (
      formattedData.paymentMethod === 'credit_card' ||
      formattedData.paymentMethod === 'threeDs'
    ) {
      return createCardToken(formattedData);
    }

    if (formattedData.paymentMethod === 'googlepay') {
      return createGooglePayToken(formattedData);
    }

    return pay(await buildPayload(formattedData))
      .then((result: any) => {
        removeAffiliateCookie();

        const payment = result.payments.find((e: any) => e);
        const { orders, openFinanceNubank } = payment;

        if (openFinanceNubank) handleOpenLink(openFinanceNubank.URL);

        const correctPaymentMethod =
          formattedData.paymentMethod === 'pix_auto' ? 'pix_auto' : payment.paymentMethod;

        setFirstPayment({
          ...payment,
          paymentMethod: correctPaymentMethod,
          status: 'waiting_payment',
          orders
        });
      })
      .catch((error: any) => {
        throw throwError(`Erro ao processar o pagamento (pay): ${error}`);
      })
      .finally(() => {
        setIsLoadingPayment(false);
      });
  };

  usePageLeave(() => {
    if (
      form.getValues('name')?.length ||
      form.getValues('email')?.length ||
      form.getValues('phone')?.length ||
      form.getValues('cpf')?.length
    ) {
      const docValue = form.getValues('cpf');
      const normalizedDoc = docValue?.replace(/\D/g, '');
      const isCnpj = normalizedDoc?.length === 14;

      createAbandonment({
        offerId: id as string,
        customerName: form.getValues('name'),
        customerEmail: form.getValues('email'),
        customerCellphone: form.getValues('phone'),
        ...(isCnpj ? { customerCnpj: docValue } : { customerCpf: docValue }),
        checkoutUrl: window.location.href
      });
    }
  });

  const getCreditCardErrorTitle = (error: string) => {
    if (error === SERVER_ERROR_MESSAGE) {
      return 'Erro interno';
    } else if (error === RECAPTCHA_ERROR_MESSAGE) {
      return '';
    } else {
      return 'Pagamento recusado';
    }
  };

  const handlePhoneErrorFocus = () => {
    if (form.formState?.errors?.phone) {
      const element = document.querySelector('[name="phone"]');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (element as HTMLElement).focus();
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey && e.key === 'v') {
      e.preventDefault();
      return;
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    return;
  };

  const watchAllFields = form.watch();
  const paymentMethod3DS = watchAllFields.paymentMethod === 'credit_card' ? 'credit' : 'debit';

  useEffect(() => {
    initializateNethoneScript();
  }, []);

  useEffect(() => {
    if (!creditCardError) return;
    setIsLoadingPayment(false);
  }, [creditCardError]);

  useEffect(() => {
    if (!form.formState.errors?.phone) return;
    handlePhoneErrorFocus();
  }, [form.formState.errors?.phone]);

  useEffect(() => {
    const paymentErrors = firstPayment?.errors?.map(({ id }) => id);
    const bumps = watchBumps?.filter(({ offer }) => paymentErrors?.includes(offer.id));
    if (bumps?.length) {
      bumps?.forEach((_, index: number) => {
        form.setValue(`bumps.${index}.checked`, true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, firstPayment]);

  useEffect(() => {
    if (!!offer?.product.bumps.length && form?.getValues('bumps')?.length === 0) {
      form.setValue('bumps', offer?.product.bumps);
    }
  }, [offer?.product.bumps]);

  useEffect(() => {
    if (!!offer?.product.bumps.length && form?.getValues('bumps')?.length === 0) {
      form.setValue('bumps', offer?.product.bumps);
    }
  }, [offer?.product.bumps]);

  const installments = useWatch({
    control: form.control,
    name: 'installments'
  });

  useEffect(() => {
    if (!installments) {
      return;
    }
    // it needs to be removed when the integrated payment is implemented
    if (canBePaidInInstallments && !hasAnySubscription) {
      const bumps = form.getValues('bumps');

      bumps?.forEach((_, index: number) => {
        form.setValue(`bumps.${index}.installments`, installments);
      });
    }
  }, [installments, canBePaidInInstallments, hasAnySubscription]);

  const shouldDisableSubmit =
    isLoadingCard ||
    isLoadingGooglePay ||
    paying ||
    totalPrice === 0 ||
    (isCreditCard && !isCardExpirationValid) ||
    (offer?.confirmEmail ? isEmailMismatch : false);

  return (
    <FormProvider {...form}>
      <form
        className="w-full sm:px-6 lg:px-8 flex justify-center"
        onSubmit={form.handleSubmit(onSubmit)}
        data-pagarmecheckout-form
      >
        <iframe
          id="challengeIframe"
          name="challengeIframe"
          data-pagarmecheckout-element="challengeIframe"
          style={{
            display: 'none',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(90vw, 500px)',
            height: 'min(80vh, 600px)',
            maxWidth: '500px',
            maxHeight: '600px',
            minWidth: '320px',
            minHeight: '400px',
            border: 'none',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            zIndex: 9999,
            backgroundColor: 'white'
          }}
        ></iframe>

        <div
          id="challengeOverlay"
          style={{
            display: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9998,
            cursor: 'pointer'
          }}
          onClick={() => {
            const iframe = document.getElementById('challengeIframe') as HTMLIFrameElement;
            const overlay = document.getElementById('challengeOverlay') as HTMLDivElement;
            if (iframe && overlay) {
              iframe.style.display = 'none';
              overlay.style.display = 'none';
              document.body.style.overflow = 'auto';
            }
          }}
        ></div>
        <div className="w-full sm:max-w-[600px] md:max-w-[720px]">
          <Card style={{ backgroundColor: settings.form?.background.color }}>
            <ProductComponent
              isLoading={isFetching}
              price={offer?.price || 0}
              product={offer?.product || ({} as any)}
              sellerEmail={offer?.sellerEmail || ''}
              type={offer?.type}
              recurrencePeriod={offer?.recurrence_period}
              paymentMethods={offer?.product.paymentMethods}
            />

            <ContactTitle color={settings.text?.color.primary} />

            <div className="flex mt-3 w-full flex-col items-start gap-6">
              <TextField
                name="name"
                size="sm"
                label="Nome completo"
                placeholder="Preencha seu nome"
                normalize
                data-pagarmecheckout-element="holder_name"
              />

              <div className="grid gap-4 w-full">
                <EmailAutoComplete
                  name="email"
                  label="Email"
                  placeholder="Preencha seu email"
                  className="w-full"
                  size="sm"
                />
                {offer?.confirmEmail && (
                  <EmailAutoComplete
                    name="confirmEmail"
                    size="sm"
                    label="Confirme seu email"
                    placeholder="Confirme seu email"
                    className="w-full"
                    onClipboardChange={handlePaste}
                    onKeyDown={handleKeyDown}
                  />
                )}
              </div>

              <div className={`grid grid-cols-1 gap-4 w-full ${showCPFOrCNPJ && 'sm:grid-cols-2'}`}>
                {showCPFOrCNPJ && (
                  <TextField
                    name="cpf"
                    size="sm"
                    label="CPF/CNPJ"
                    cpfCnpj
                    placeholder="Preencha seu CPF/CNPJ"
                  />
                )}
                <TextFieldPhoneNumber
                  name="phone"
                  size="sm"
                  label={getLabel.phone()}
                  placeholder="Preencha seu celular"
                />
              </div>

              <AboutCpf />

              {offer?.product.showAddressFields && (
                <section className="mt-6 w-full">
                  <AddressForm />
                </section>
              )}

              <section className="mt-6 w-full">
                <PaymentTitle color={settings.text?.color.primary} />
                <PaymentMethods />
              </section>

              {Boolean(bumpsArray.fields.length) && (
                <>
                  <BumpOfferTitle
                    color={settings.text?.color.primary}
                    count={bumpsArray.fields.length}
                  />

                  <section className="w-full flex flex-col gap-3">
                    {bumpsArray.fields.map((_, index) => (
                      <BumpItem
                        key={index}
                        index={index}
                        defaultChecked={!!firstPayment?.errors?.map(({ id }) => id)?.length}
                      />
                    ))}
                  </section>
                </>
              )}

              {!!creditCardError?.length && (
                <Alert
                  title={getCreditCardErrorTitle(creditCardError)}
                  message={creditCardError}
                  type="error"
                />
              )}

              <section className="mt-6 w-full">
                <OrderSummary />
              </section>

              <section className="mt-6 flex w-full justify-center">
                <Button
                  type="submit"
                  disabled={shouldDisableSubmit}
                  loading={isLoadingCard || isLoadingGooglePay || paying || isLoadingPayment}
                  className="w-full max-w-[800px]"
                  backgroundColor={settings?.payButton?.color}
                  textColor={settings?.payButton?.text?.color}
                >
                  <span className="text-[18px]" style={{ color: settings?.payButton?.text?.color }}>
                    {settings.payButton.text.text}
                  </span>
                </Button>
              </section>
            </div>

            <div className="flex flex-col justify-center gap-2 pt-8 text-center">
              {isNommi ? (
                <img
                  src={`${BRANDING_URL}name-logo-full-white.png`}
                  alt="nommi"
                  className="mx-auto w-20 grayscale"
                  style={{ filter: hexToFilter(settings.text.color.primary) }}
                />
              ) : (
                <img
                  src="logo/cakto-logo-full-new.png"
                  alt="cakto"
                  className="mx-auto"
                  width={100}
                />
              )}
              <p className="mt-4 text-sm" style={{ color: settings.text?.color.primary }}>
                {getLabel.brandName()} está processando este pagamento para o vendedor{' '}
                <a
                  className="font-bold underline"
                  href={`mailto:${offer?.sellerEmail}`}
                  style={{ color: settings.text?.color.primary }}
                >
                  {offer?.sellerName || ''}
                </a>
              </p>
              <p className="mt-2 text-xs flex items-center justify-center text-[#23CC67]">
                <CheckCircleIcon width={16} height={16} className="inline-block mr-1" />
                Compra 100% segura
              </p>
              <p className="mt-3 text-xs" style={{ color: settings.text?.color.primary }}>
                Este site é protegido pelo reCAPTCHA do Google
              </p>
              <p className="mt-2 text-xs" style={{ color: settings.text?.color.primary }}>
                <a className="font-bold" href="https://www.cakto.com.br/politica-de-privacidade">
                  Política de privacidade
                </a>{' '}
                e{' '}
                <a className="font-bold" href="#">
                  Termos de serviço
                </a>
              </p>
              <p className="mt-2 text-xs" style={{ color: settings.text?.color.primary }}>
                * Parcelamento com acréscimo
                <br />
                <br />
                Ao continuar, você concorda com os{' '}
                <a
                  className="font-bold"
                  href="https://www.cakto.com.br/termo-de-compra-e-venda"
                  style={{ color: settings.text?.color.primary }}
                >
                  Termos de Compra
                </a>
              </p>
            </div>
          </Card>
        </div>
      </form>
    </FormProvider>
  );
};

export default CheckoutForm;
